import { IOvToChannelMap, Page } from "../../types/page";
import { ComponentNames } from "../../constant/component-constant";
import { arraytoObj, cleanEmpty, commonSbFixer } from "../../helper/sb-fixer";
import { isChannelsServiceEnabled, pickChannels, pickChrome, buildLineupConfig } from "../../helper/channels-service-helpers";
import { channelsInteractiveToolAdapterV2 } from "./channels-interactive-tool-adapter.v2";
import {
  IApplicationFeatureConfig,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IMyFreeDTVConfig
} from "../../types/application-feature-config";
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IChannelConfig,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IEmbeddedSvodOffers,
  IEpochOffers,
  IGlobalSBFeeds,
  IPackageConfig
} from "../../types/global";
import { pagePropertiesAdapter } from "./page-properties.adapter";
import {
  IPagesCache,
  IPersonalizedComponent,
  IPersonalizedComponentVariant,
  IPersonalizationRedirect
} from "../../types/cache";
import { channelPackLineupAdapeter } from "./channel-pack-lineup-adapter";
import { inpagechannelPackLineupAdapeter } from "./inpage-channel-pack-lineup-adapter";
import { PackageCompareToolAdapeter } from "./package-compare-tool-adapeter";
import { ChannelLineUpPageAdapter } from "./channel-lineup-page-adapter";
import { ChannelCompareToolPageAdapter } from "./channel-compare-tool-page-adapter";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { satelliteChannelLineupAdapter } from "./satellite-channel-lineup-adapter";
import { genrePreviewToolAdapater } from "./genre-preview-tool-adapter";
import { conspiracyBoardAdapter } from "./conspiracy-board.adapetr";
import { rsnLookupToolAdapter } from "./rsn-lookup-tool-adapter";
import { equipmentWizardAdapter } from "./equipment-wizard-adapter";
import { breadcrumbAdapter } from "./breadcrumb-adapater";
import { getAppHostUrl } from "../../helper/get-app-host-url";
import { planCardAdapter } from "./plan-card-adapter";
import { dtvMarqueeAdapter } from "./dtv-marquee-adapter";
import { dtvCompetitorComparisonTableAdapter } from "./dtv-competitor-comparison-table-adpater";
import { packageCarouselAdapter } from "./package-carousel-adapter";
import { flipCardsAdapter } from "./flip-cards-adapter";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { logError, logAdapterEvent, logGenericEvent } from "../../helper/logger";
import { dtvAgentPackageCompareToolAdapter } from './dtv-agent-package-compare-tool-adapter';
import { cloneDeep } from "lodash";
import { genrePackDetailsPanelAdapter } from "./genre-pack-details-panel-adapter";
import { rotatingMarqueeAdapter } from "./rotating-marquee-adapter";
import { marketingBannerAdapter } from "./marketing-banner-adapter";
import { dtvCompetitorTableAdapter } from "./dtv-competitor-table-adapter";

const ADAPTER_NAME = "PageAdapter";

interface PageStory {
  story: {
    name: string;
    created_at?: string;
    published_at?: string;
    updated_at?: string;
    id?: number;
    uuid?: string;
    content: {
      Body: ComponentData[];
      personalizationRedirects?: IPersonalizationRedirect[];
      pznRedirects?: IPersonalizationRedirect[];
    };
    slug?: string;
    full_slug?: string;
    sort_by_date?: string | null;
    position?: number;
    tag_list?: string[];
    is_startpage?: boolean;
    parent_id?: number;
    meta_data?: any | null;
    group_id?: string;
    first_published_at?: string;
    release_id?: any | null;
    lang?: string;
    path?: string;
    alternates?: any[];
    default_full_slug?: any | null;
    translated_slugs?: any | null;
  };
  cv?: number;
  rels?: any[];
  links?: any[];
}

interface ComponentData {
  id?: string;
  type?: string;
  component?: string;
  _uid?: string;
  partnerName?: string;
}

interface IPageCache {
  value: PageStory,
  cacheKey: string;
}

interface IPageAdapter {
  pagesCache: IPageCache[];
  pagesInfo: Page[];
  batchPagesInfo?: Page[];
  chnlListinPageData?: any;
  channelCompareToolPageData?: any;
  satelliteChannelListingPageData?: any;
  applicationFeatureConfig: IApplicationFeatureConfig;
  ovToChannelMapData: IOvToChannelMap;
  epochOffers: IEpochOffers;
  packagesConfigFeed?: any; // ToDo add proper interface for entire packagesConfigFeed
  genrePackOfferIds?: any;
  onDemandPackageConfig?: Array<IPackageConfig>
  svodGenreData?: any;
  svodGenreAddon?: any;
  globalSBFeeds?: IGlobalSBFeeds;
  sbConfig?: any;
  rsnTierLegalTextObj?: any;
  competitorComparison?: any;
  /**
   * channels-service v4 envelope for the ott platform.
   * Only consumed when USE_CHANNELS_SERVICE=TRUE.
   * Shape: `{ platform, lastUpdated, channels[] }`.
   */
  channelsServiceOtt?: any;
  /**
   * channels-service v4 envelope for the satellite platform.
   * Only consumed when USE_CHANNELS_SERVICE=TRUE.
   */
  channelsServiceSatellite?: any;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ComponentDataStructure {
  config?: any;
  props: {
    data?: any;
    options?: {
      isAuthenticatedUser?: any;
      recognizedUserData?: any;
    };
    [key: string]: any;
  };
  type: ComponentNames;
  variationID?: any;
}

const NON_VISUAL_COMPONENTS = ["PAGEBACKGROUNDS"];

export function pageAdapter(args: IPageAdapter): IPagesCache {
  try {
    logAdapterEvent(ADAPTER_NAME, "starting pages adaption", {
      pagesCount: args?.pagesCache?.length,
      pagesInfoCount: args?.batchPagesInfo?.length
    });
    const {
      pagesCache,
      pagesInfo,
      chnlListinPageData,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      channelCompareToolPageData,
      satelliteChannelListingPageData,
      applicationFeatureConfig,
      ovToChannelMapData,
      epochOffers,
      packagesConfigFeed,
      genrePackOfferIds,
      onDemandPackageConfig,
      svodGenreData,
      svodGenreAddon,
      globalSBFeeds,
      sbConfig,
      rsnTierLegalTextObj,
      competitorComparison,
      channelsServiceOtt,
      channelsServiceSatellite
    } = args;
    const pagesData: IPagesCache = {} as IPagesCache;
    // `parseGlobalSBFeed`'s `cleanEmpty` strips the `array_` prefix, so read the
    // un-prefixed names here (the SB blok keeps the `array_` authoring name).
    const packagesConfig = packagesConfigFeed?.packageConfig;
    // Chrome/CCT config from the global package-config feed. Gated to flag ON:
    // under flag OFF this stays `undefined` so every adapter read falls through to
    // the per-page `content.*` (AEM) path unchanged. The object literal lives in
    // `buildLineupConfig` so the request-time `dynamic-page-components-adapter`
    // builds a byte-identical config from the same feed.
    const globalLineupConfig = isChannelsServiceEnabled()
      ? buildLineupConfig(packagesConfigFeed, packagesConfig)
      : undefined;

    pagesCache?.forEach((entry, index) => {
      const { value: pageStory, cacheKey: cmsPageName } = entry;
      try {
        logAdapterEvent(
          ADAPTER_NAME,
          `adapting page: ${cmsPageName || "unknown"}`,
          { pageIndex: index }
        );
        const pageComponents = []; // : ComponentDataStructure[]; // TO-DO: commented out for dev only
        let pageProperties: any;
        let rawPageProperties: any;
        const personalizedComponents: IPersonalizedComponent[] = [];
        let pageTheme: any;
        let pageBackground: any;
        let flashComponent: any;
        let oem: any;
        const queryParamsRequired: any = [];
        let pageLevelGlobalHeader: any = {};
        let pageLevelLinkFarm: any = {};
        let pageLevelGlobalFooter: any = {};

        let compIndex = -1;
        const content = pageStory?.story?.content;
        const personalizationRedirects: IPersonalizationRedirect[] = [...(content?.personalizationRedirects || content?.pznRedirects || [])]
          .sort((a, b) => parseInt(b?.priority || "0", 10) - parseInt(a?.priority || "0", 10));
        let pageDataArr: any = [];
        if (Array.isArray(content?.Body) && content.Body.length > 0) {
          pageDataArr = content.Body;
        } else if (sbConfig?.sbPreviewAllowed && content && (content as any).component && (!content.Body || !Array.isArray(content.Body))) {
          pageDataArr = [content];
        }
        const pageData: ComponentData[] =
          pageDataArr?.reduce?.(
            (a, v, i) => ({
              ...a,
              [`${v?.component}-${i}`]: v
            }),
            []
          );

        if (pageData) {
          let dtviOffersPrices = { ...epochOffers?.dtviOffersPrices };
          let affiliatePartner = "";
          try {
            const oemComponent = Object.values(pageData).find(
              component => component.type === "OEM"
            );
            const affiliatesConfig = applicationFeatureConfig?.affiliatesConfig || [];
            if (oemComponent?.partnerName && affiliatesConfig?.includes(oemComponent?.partnerName)) {
              const dtviAffiliatesOfferPrices = { ...epochOffers?.dtviAffiliatesOfferPrices || {} };
              if (dtviAffiliatesOfferPrices[oemComponent?.partnerName]) {
                affiliatePartner = oemComponent?.partnerName;
                dtviOffersPrices = {
                  ...dtviOffersPrices,
                  offerPrices: dtviAffiliatesOfferPrices[affiliatePartner]?.offerPrices,
                  dtviPackagesList: dtviAffiliatesOfferPrices[affiliatePartner]?.dtviPackagesList
                };
                logAdapterEvent(
                  ADAPTER_NAME,
                  `Injected Affiliates Partner Special Prices for page: ${cmsPageName}`,
                  { partnerName: affiliatePartner }
                );
              }
            }
          } catch (error) {
            logError(ADAPTER_NAME, error, { step: "Error handling affiliate partner offers" });
          }

          const {
            breadcrumb,
            breadcrumbTitle,
            isDoubleBreadcrumbNeeded,
            disableBreadCrumbsTop,
            disableBreadCrumbs
          } = getBreadcrumbdata(pageData) || {};
          if (breadcrumb) {
            pageData[breadcrumb.type] = breadcrumb;
          }

          const keys = Object.keys(pageData || {}).filter(key => (
            typeof pageData[key] === "object" &&
              pageData[key]?.skipRender !== true
          ));

          keys.forEach((key, index) => {
            try {
              let component: any;
              let data: any;

              data = pageData[key];
              if (sbConfig?.sbPreviewAllowed && data?.reference?.content) {
                const { content } = data.reference;
                const wrapperUid = data._uid;
                const wrapperEditable = data._editable;
                data = {
                  ...content,
                  _uid: content._uid ?? wrapperUid,
                  _editable: content._editable ?? wrapperEditable,
                  __ref_uid: wrapperUid,
                  __ref_editable: wrapperEditable
                }
              } else {
                data = data?.reference?.content ? data?.reference?.content : data;
              }

              /** ----- Common Fixer Start */
              if (data) {
                const componentType = data.type;
                let personalizationKey = "";

                if (!NON_VISUAL_COMPONENTS.includes(componentType)) {
                  compIndex += 1;
                }

                if (data.component === "personalizedContent") {
                  logAdapterEvent(
                    ADAPTER_NAME,
                    `handling personalized content for component: ${key} on page: ${cmsPageName}`,
                    { componentIndex: compIndex }
                  );
                  const personalizationData = getPersonalizedData(data);
                  const { defaultVariants, defaultAnalytics, personalizationComponents } =
                    personalizationData;

                  // Handle null or undefined defaultVariants
                  data = defaultVariants || {};

                  personalizationKey = `personalizedContent-${index}`;

                  if (personalizationComponents?.length > 0) {
                    const parsedPersonalizationComponents: IPersonalizedComponentVariant[] =
                      personalizationComponents.map(item => ({
                        ...item,
                        components: [
                          adaptPersonalizedVariantComponent(item?.components, {
                            index,
                            sbPreviewAllowed: sbConfig?.sbPreviewAllowed,
                            modalPriorityMap: rawPageProperties?.modalPriorityMap,
                            packagesConfig,
                            cmsPageName,
                            rsnTierLegalTextObj,
                            epochOffers,
                            dtviOffersPrices,
                            svodGenreData,
                            embeddedSvods:
                              globalSBFeeds?.embeddedSvods?.subscriptions || []
                          })
                        ]
                      }));

                    personalizedComponents.push({
                      type: componentType,
                      key: personalizationKey,
                      variants: parsedPersonalizationComponents,
                      defaultAnalytics
                    });
                  }
                }
                // Flag ON: normalise the raw SB blok through the standard
                // arraytoObj+cleanEmpty pipeline (collapses length-1 arrays,
                // strips array_ prefixes) so the shape matches the
                // transformAEMToSB output the channel adapters consume under
                // flag OFF. Flag OFF passes `data` through untouched.
                const normalizeChannelContent = (d: any) =>
                  isChannelsServiceEnabled() ? cleanEmpty(arraytoObj(d)) : d;
                switch (data?.type) {
                  case ComponentNames.BREADCRUMBS:
                  case ComponentNames.BREADCRUMBSV2:
                    // eslint-disable-next-line no-case-declarations
                    const pages = convertArrayObjectToObject(
                      pagesInfo,
                      "cacheKey"
                    );
                    component = breadcrumbAdapter({
                      breadCrumbsContent: {
                        ...data,
                        isDoubleBreadcrumbNeeded,
                        breadcrumbTitle
                      },
                      configuration: {
                        breadcrumbHomeURL: getAppHostUrl()
                      },
                      cmsPageName,
                      pages,
                      cms_type: "sb"
                    });
                    break;
                  case ComponentNames.PAGEPROPERTY:
                    data = commonSbFixer(data);
                    pageProperties = pagePropertiesAdapter({
                      pageProperties: cloneDeep(data?.props),
                      cmsPageName
                    });

                    rawPageProperties = data?.props;

                    break;

                  case ComponentNames.PAGETHEME:
                    data = commonSbFixer(data);
                    pageTheme = data?.props;
                    break;

                  case ComponentNames.PAGEBACKGROUNDS:
                    data = commonSbFixer(data);
                    pageBackground = data?.props;
                    break;

                  case ComponentNames.DTVFLASH:
                    data = commonSbFixer(data);
                    flashComponent = data?.props;
                    break;

                  case ComponentNames.OEM:
                    data = commonSbFixer(data);
                    oem = data.props;
                    break;

                  case ComponentNames.GLOBALHEADER:
                    data = commonSbFixer(data);
                    pageLevelGlobalHeader = data.props;
                    break;
                  case ComponentNames.GLOBALFOOTER:
                    data = commonSbFixer(data);
                    pageLevelGlobalFooter = data.props;
                    break;
                  case ComponentNames.LINKFARM:
                    data = commonSbFixer(data);
                    pageLevelLinkFarm = data.props;
                    break;
                  case ComponentNames.DTVMARQUEE:
                    component = dtvMarqueeAdapter({
                      content: data,
                      svodGenreData,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      epochOffers,
                      cmsPageName,
                      packagesConfig
                    });
                    break;
                  case ComponentNames.COMPETITORCOMPARISONTABLEV2:
                    component = dtvCompetitorComparisonTableAdapter({
                      content: data,
                      competitorComparison,
                      genreSvodOffers: epochOffers.genreSvodOffers,
                      packageConfig: packagesConfig
                    });
                    break;
                  case ComponentNames.CHLUP_INTERACTIVE_TOOL:
                    component = channelsInteractiveToolAdapterV2({
                      index,
                      content: data,
                      chnlListinPageData,
                      myFreeDTVConfig:
                        applicationFeatureConfig?.myFreeDTVConfig?.[0],
                      displayGenrePack:
                        applicationFeatureConfig?.displayGenrePacks,
                      displayGenrePackSatellite:
                        applicationFeatureConfig?.displayGenrePacksSatellite,
                      genreLocalChannelMapping:
                        applicationFeatureConfig?.genreLocalChannelMapping,
                      signatureExcludedLocalChannels:
                        applicationFeatureConfig?.signatureExcludedLocalChannels,
                      genreConfig:
                        applicationFeatureConfig?.genreConfig?.[0],
                      ovChnlPageData: ovToChannelMapData,
                      genreSvodOffers: epochOffers.genreSvodOffers,
                      svodGenreData,
                      svodGenreAddon,
                      packageConfig: packagesConfig,
                      genreNationals: globalSBFeeds?.genreNationals,
                      cmsPageName,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      dtviEmbeddedIncludedSvods:
                        epochOffers.dtviEmbeddedSvodOffers,
                      dtvsEmbeddedIncludedSvods:
                        epochOffers.dtvsEmbeddedSvodOffers,
                      dtviSvodOffers: epochOffers.dtviSvodOffers,
                      dtvsSvodOffers: epochOffers.dtvsSvodOffers,
                      svodDtviPcp: globalSBFeeds?.svodDtviPcp,
                      svodDtvsPcp: globalSBFeeds?.svodDtvsPcp,
                      rsnTierLegalTextObj,
                      // DTVS/satellite variant needs the satellite envelope — feeding OTT
                      // drops every satellite channel at the isSatelliteContext gate
                      // (channels.ts treats both "dtvclup" and "satelliteclup" as satellite).
                      channelsServiceData: ["dtvclup", "satelliteclup"].includes(
                        data?.chnlSrc?.toLowerCase()
                      )
                        ? channelsServiceSatellite
                        : channelsServiceOtt,
                      globalLineupConfig,
                    });
                    break;
                  case ComponentNames.PACKAGECOMPARETOOL:
                    component = PackageCompareToolAdapeter({
                      content: data,
                      chnlListingPackages: pickChrome(
                        globalLineupConfig?.packages,
                        chnlListinPageData?.[":items"]?.chnlListingPageData?.elements?.packages
                      ),
                      chnlListingChannels: pickChannels(
                        channelsServiceOtt?.channels,
                        chnlListinPageData?.[":items"]?.chnlListingPageData?.elements?.channels
                      ),
                      chnlListingMiniPacks: pickChrome(
                        globalLineupConfig?.miniPacks,
                        chnlListinPageData?.[":items"]?.chnlListingPageData?.elements?.miniPacks
                      )?.options,
                      genreOffers: epochOffers.genreSvodOffers,
                      svodGenreData,
                      packageConfig: packagesConfig,
                      genrePackOfferIds,
                      onDemandPackageConfig,
                      genreLocalChannelMapping:
                        applicationFeatureConfig?.genreLocalChannelMapping,
                      signatureExcludedLocalChannels:
                        applicationFeatureConfig?.signatureExcludedLocalChannels,
                      getOffersData: dtviOffersPrices,
                      myFreeDTVConfig:
                        applicationFeatureConfig?.myFreeDTVConfig?.[0],
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      dtviEmbeddedSvodOffers:
                        epochOffers.dtviEmbeddedSvodOffers,
                      queryParams: {},
                      genreNationals: globalSBFeeds?.genreNationals,
                      creditOffers: epochOffers?.genreSvodOffers?.creditOffers
                    });
                    queryParamsRequired.push("genrePacks", "selectPackage", "mode");
                    break;

                  case ComponentNames.DTVAGENTPACKAGECOMPARETOOL:
                    component = dtvAgentPackageCompareToolAdapter({
                      content: cleanEmpty(arraytoObj(data)),
                      channelListingData: chnlListinPageData,
                      rsnTierLegalText: rsnTierLegalTextObj,
                      genreLocalChannelMapping: applicationFeatureConfig?.genreLocalChannelMapping,
                      signatureExcludedLocalChannels:
                        applicationFeatureConfig?.signatureExcludedLocalChannels,
                      embeddedSvods: globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      embeddedIncludedSvods: epochOffers.dtviEmbeddedSvodOffers,
                      getOffersGenreData: epochOffers?.genreSvodOffers,
                      offerPrices: epochOffers?.integrationPartnerOffersPrices,
                      genreNationals: globalSBFeeds?.genreNationals,
                      packageConfig: packagesConfig,
                      satelliteBasePlans: epochOffers?.satelliteBasePlans,
                      satelliteAddonsByBP: epochOffers?.satelliteAddonsByBP,
                      channelsServiceData: channelsServiceOtt,
                    });
                    break;

                  case ComponentNames.CHANNELPACKLINEUP:
                    component = channelPackLineupAdapeter({
                      content: data,
                      channelListingData: chnlListinPageData,
                      genreOffers: epochOffers.genreSvodOffers,
                      svodGenreData,
                      genreLocalChannelMapping:
                        applicationFeatureConfig?.genreLocalChannelMapping,
                      localChannelsInPackage: applicationFeatureConfig?.localChannelsInPackage?.[0],
                      packageConfig: packagesConfig,
                      onDemandPackageConfig,
                      myFreeDTVConfig:
                        applicationFeatureConfig?.myFreeDTVConfig?.[0],
                      genreNationals: globalSBFeeds?.genreNationals,
                      cmsPageName,
                      channelsServiceData: channelsServiceOtt,
                      globalLineupConfig,
                    });
                    queryParamsRequired.push("pkg");
                    break;

                  case ComponentNames.INPAGECHANNELPACKLINEUP:
                    component = inpagechannelPackLineupAdapeter({
                      content: data,
                      channelListingData: chnlListinPageData,
                      svodGenreData,
                      genreLocalChannelMapping:
                        applicationFeatureConfig?.genreLocalChannelMapping,
                      signatureExcludedLocalChannels:
                        applicationFeatureConfig?.signatureExcludedLocalChannels,
                      packageConfig: packagesConfig,
                      myFreeDTVConfig:
                        applicationFeatureConfig?.myFreeDTVConfig?.[0],
                      genreNationals: globalSBFeeds?.genreNationals,
                      cmsPageName,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      dtviEmbeddedIncludedSvods:
                        epochOffers.dtviEmbeddedSvodOffers,
                      dtvsEmbeddedIncludedSvods:
                        epochOffers.dtvsEmbeddedSvodOffers,
                      genreSvodOffers: epochOffers.genreSvodOffers,
                      channelsServiceData: channelsServiceOtt,
                    });
                    break;

                  case ComponentNames.SATELLITE_CHANNEL_LISTING_PAGE:
                    component = ChannelLineUpPageAdapter({
                      content: normalizeChannelContent(data),
                      componentId: data.id,
                      index,
                      myFreeDTVConfig:
                        applicationFeatureConfig?.myFreeDTVConfig?.[0],
                      // genreLocalChannelMapping:
                      //   applicationFeatureConfig?.genreLocalChannelMapping,
                      packageConfig: packagesConfig,
                      // enableGenrePackChannelView:
                      //   applicationFeatureConfig?.enableGenrePackChannelView,
                      // svodGenreData,
                      // genreOffers: epochOffers.genreSvodOffers,
                      // genreNationals: globalSBFeeds?.genreNationals,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      dtvsEmbeddedSvodOffers:
                        epochOffers.dtvsEmbeddedSvodOffers,
                      isEmbeddedSvodsEnabled:
                        applicationFeatureConfig?.embeddedSvodsConfig?.[0]
                          ?.enableSatelliteSVODSForChlup,
                      queryParams: {},
                      rsnTierLegalTextObj,
                      isSatelliteChlup: true,
                      channelsServiceData: channelsServiceSatellite,
                      aemChannels:
                        satelliteChannelListingPageData?.[":items"]
                          ?.chnlListingPageData?.elements?.channels,
                      aemChannelCategories:
                        satelliteChannelListingPageData?.[":items"]
                          ?.chnlListingPageData?.elements?.channelCategories,
                      globalLineupConfig
                    });

                    break;

                  case ComponentNames.CHANNEL_LISTING_PAGE:
                    component = ChannelLineUpPageAdapter({
                      content: normalizeChannelContent(data),
                      componentId: data.id,
                      index,
                      myFreeDTVConfig:
                        applicationFeatureConfig?.myFreeDTVConfig?.[0],
                      genreLocalChannelMapping:
                        applicationFeatureConfig?.genreLocalChannelMapping,
                      packageConfig: packagesConfig,
                      enableGenrePackChannelView:
                        applicationFeatureConfig?.enableGenrePackChannelView,
                      svodGenreData,
                      genreOffers: epochOffers.genreSvodOffers,
                      genreNationals: globalSBFeeds?.genreNationals,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      dtviEmbeddedSvodOffers:
                        epochOffers.dtviEmbeddedSvodOffers,
                      isEmbeddedSvodsEnabled:
                        applicationFeatureConfig?.embeddedSvodsConfig?.[0]
                          ?.enableForChlup,
                      queryParams: {},
                      rsnTierLegalTextObj,
                      channelsServiceData: channelsServiceOtt,
                      aemChannels:
                        chnlListinPageData?.[":items"]?.chnlListingPageData
                          ?.elements?.channels,
                      aemChannelCategories:
                        chnlListinPageData?.[":items"]?.chnlListingPageData
                          ?.elements?.channelCategories,
                      globalLineupConfig
                    });
                    // SATELLITE_CHANNEL_LISTING_PAGE never uses ?view= so it intentionally does not push here.
                    queryParamsRequired.push("view");

                    break;

                  case ComponentNames.CHANNEL_COMPARISON_TOOL:
                    component = ChannelCompareToolPageAdapter({
                      content: normalizeChannelContent(data),
                      componentId: data.id,
                      cmsPageName: "",
                      index,
                      myFreeDTVConfig:
                        applicationFeatureConfig?.myFreeDTVConfig?.[0],
                      msbNationalRollOut:
                        applicationFeatureConfig?.msbNationalRollOut,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [], // EmbeddedSVODs -> CMS Data
                      dtviEmbeddedSvodOffers:
                        epochOffers?.dtviEmbeddedSvodOffers, // EmbeddedSVODs -> EPOCH Data
                      dtvsEmbeddedSvodOffers:
                        epochOffers?.dtvsEmbeddedSvodOffers, // DTVS EmbeddedSVODs -> EPOCH Data
                      isEmbeddedSvodsEnabled:
                        applicationFeatureConfig?.embeddedSvodsConfig?.[0]
                          ?.enableForChlup,
                      queryParams: {},
                      genreNationals: globalSBFeeds?.genreNationals,
                      // /channel-lineup/modal renders columns for BOTH stream and
                      // satellite packages on the same page (Akamai redirect from
                      // satellite). Pass both platforms' channels-service envelopes
                      // — adapter unions them deduped by channel identity.
                      channelsServiceData: channelsServiceOtt,
                      channelsServiceSatelliteData: channelsServiceSatellite,
                      aemChannels:
                        channelCompareToolPageData?.[":items"]?.chnlCmpToolData
                          ?.elements?.channels,
                      aemChannelCategories:
                        channelCompareToolPageData?.[":items"]?.chnlCmpToolData
                          ?.elements?.channelCategories,
                      globalLineupConfig
                    });
                    queryParamsRequired.push(
                      "channels",
                      "lang",
                      "myFreeDtv",
                      "edsp",
                      "inPage",
                      "cmprPkg",
                      "modalSrc",
                      "showCTA",
                      "isLocal",
                      "tab",
                      "category",
                      "pageView",
                      "showPkg",
                      "pkg"
                    );
                    break;

                  case ComponentNames.GENREPREVIEWTOOL:
                    component = genrePreviewToolAdapater({
                      componentProps: data,
                      index,
                      genreSvodOffers: epochOffers.genreSvodOffers,
                      svodGenreData,
                      packageConfig: packagesConfig,
                      cmsPageName
                    });
                    break;

                  case ComponentNames.CONSPIRACYBOARD:
                    component = conspiracyBoardAdapter({
                      content: data,
                      componentId: data.id,
                      index,
                      cmsPageName
                    });
                    break;

                  case ComponentNames.RSNLOOKUPTOOL:
                    component = rsnLookupToolAdapter({
                      content: data,
                      componentId: data.id,
                      index,
                      queryParams: {},
                      channelListingData: chnlListinPageData,
                      channelsServiceData: channelsServiceOtt,
                      genreLocalChannelMapping:
                        applicationFeatureConfig?.genreLocalChannelMapping,
                      cmsPageName
                    });

                    queryParamsRequired.push(
                      "showchannels",
                      "showsignaturepackages",
                      "zipCode"
                    );

                    break;

                  case ComponentNames.LOCALRSNTOOL: {
                    component = commonSbFixer(
                      data,
                      index,
                      sbConfig?.sbPreviewAllowed,
                      rawPageProperties?.modalPriorityMap,
                      packagesConfig,
                      cmsPageName,
                      rsnTierLegalTextObj
                    );
                    if (
                      component?.props &&
                      applicationFeatureConfig?.signatureExcludedLocalChannels
                    ) {
                      component.props.signatureExcludedLocalChannels =
                        applicationFeatureConfig.signatureExcludedLocalChannels;
                    }
                    break;
                  }

                  case ComponentNames.EQUIPMENTWIZARD:
                    component = equipmentWizardAdapter({
                      content: cleanEmpty(arraytoObj(data)),
                      componentId: data.id,
                      cmsPageName
                    });
                    break;
                  case ComponentNames.PLANCARD: {
                    component = planCardAdapter({
                      content: cleanEmpty(arraytoObj(data)),
                      getOffersData: dtviOffersPrices,
                      getOffersDTVsData: epochOffers.dtvsOffersPrices,
                      packageConfig: packagesConfig,
                      index,
                      genreSvodOffers: epochOffers.genreSvodOffers,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      dtviEmbeddedSvodOffers:
                        epochOffers.dtviEmbeddedSvodOffers,
                      dtvsEmbeddedSvodOffers:
                        epochOffers.dtvsEmbeddedSvodOffers,
                      cmsPageName,
                      byodPlanCardOffers: epochOffers.byodPlanCardOffers,
                      ...(affiliatePartner && { affiliatePartner })
                    });
                    break;
                  }
                  case ComponentNames.ROTATINGMARQUEE: {
                    component = rotatingMarqueeAdapter({
                      data,
                      index,
                      sbPreviewAllowed: sbConfig?.sbPreviewAllowed,
                      modalPriorityMap: rawPageProperties?.modalPriorityMap,
                      packagesConfig,
                      cmsPageName,
                      rsnTierLegalTextObj,
                      offerPrices: epochOffers?.genreSvodOffers?.offerPrices
                    });
                    break;
                  }
                  case ComponentNames.PACKAGECAROUSEL: {
                    component = packageCarouselAdapter({
                      content: cleanEmpty(arraytoObj(data)),
                      chnlListinPageData,
                      index,
                      cmsPageName,
                      packageConfig: packagesConfig,
                      getOffersData: dtviOffersPrices,
                      getOffersDTVsData: epochOffers.dtvsOffersPrices,
                      genreSvodOffers: epochOffers.genreSvodOffers,
                      svodGenreData,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      dtviEmbeddedSvodOffers:
                        epochOffers.dtviEmbeddedSvodOffers,
                      dtvsEmbeddedSvodOffers:
                        epochOffers.dtvsEmbeddedSvodOffers,
                      globalCards: globalSBFeeds?.globalPackageCarousel?.cards || [],
                      channelsServiceData: channelsServiceOtt,
                    });
                    break;
                  }

                  case ComponentNames.FLIPCARDS: {
                    component = flipCardsAdapter({
                      content: cleanEmpty(arraytoObj(data)),
                      packageConfig: packagesConfig,
                      genreSvodOffers: epochOffers.genreSvodOffers,
                      getOffersData: dtviOffersPrices,
                      getOffersDTVsData: epochOffers.dtvsOffersPrices,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      dtviEmbeddedSvodOffers:
                        epochOffers.dtviEmbeddedSvodOffers,
                      dtvsEmbeddedSvodOffers:
                        epochOffers.dtvsEmbeddedSvodOffers,
                      byodPlanCardOffers: epochOffers.byodPlanCardOffers
                    });
                    break;
                  }

                  case ComponentNames.COMPETITORTABLE:
                    component = dtvCompetitorTableAdapter({
                      content: data,
                      genreSvodOffers: epochOffers.genreSvodOffers,
                      packageConfig: packagesConfig,
                      cmsPageName
                    });
                    break;

                  case ComponentNames.GENREPACKDETAILSPANEL:
                    component = genrePackDetailsPanelAdapter({
                      content: data,
                      svodGenreData,
                      embeddedSvods:
                        globalSBFeeds?.embeddedSvods?.subscriptions || [],
                      epochOffers,
                      cmsPageName
                    });
                    break;

                  case ComponentNames.MARKETINGBANNER: {
                    component = marketingBannerAdapter({
                      content: data,
                      index,
                      sbPreviewAllowed: sbConfig?.sbPreviewAllowed,
                      modalPriorityMap: rawPageProperties?.modalPriorityMap,
                      packagesConfig,
                      cmsPageName,
                      rsnTierLegalTextObj,
                      genreSvodOffers: epochOffers?.genreSvodOffers,
                      getOffersData: dtviOffersPrices,
                      getOffersDTVsData: epochOffers?.dtvsOffersPrices,
                      packageConfig: packagesConfig
                    });
                    break;
                  }

                  case ComponentNames.PACKAGECOMPARISONCALCULATOR: {
                    // WAT-11166: the calculator fetches its offers client-side, so it
                    // only needs the same standard cleanup as the default path — plus
                    // the centralized global device-fee fallback (packages-config
                    // dtviDeviceFee, the same value the server dtviOffersResolver uses).
                    // The component uses it only when EPOCH returns no live video-device price.
                    component = commonSbFixer(
                      data,
                      index,
                      sbConfig?.sbPreviewAllowed,
                      rawPageProperties?.modalPriorityMap,
                      packagesConfig,
                      cmsPageName,
                      rsnTierLegalTextObj
                    );
                    if (component?.props) {
                      component.props.deviceFeeFallback =
                        packagesConfigFeed?.dtviDeviceFee;
                    }
                    break;
                  }

                  default: {
                    component = commonSbFixer(
                      data,
                      index,
                      sbConfig?.sbPreviewAllowed,
                      rawPageProperties?.modalPriorityMap,
                      packagesConfig,
                      cmsPageName,
                      rsnTierLegalTextObj
                    );
                    break;
                  }
                }
                if (component && (component.type || component.props?.type || personalizationKey)) {
                  if(personalizationKey){
                    component.personalizationKey = personalizationKey;
                  }
                  pageComponents.push(component);
                }
              }
            } catch (error) {
              logError(ADAPTER_NAME, error, {
                page: cmsPageName,
                componentKey: key,
                componentIndex: index
              });
            }
          });

          if (pageComponents) {
            handleBreadcrumbs(
              pageComponents,
              disableBreadCrumbs,
              disableBreadCrumbsTop,
              applicationFeatureConfig
            );

            pagesData[cmsPageName] = {
              components: pageComponents,
              pageProperties,
              rawPageProperties,
              personalizedComponents,
              personalizationRedirects,
              pageTheme,
              pageBackground,
              flashComponent,
              oem,
              pageLevelGlobalHeader,
              pageLevelLinkFarm,
              pageLevelGlobalFooter,
              queryParamsRequired
            };

            logAdapterEvent(
              ADAPTER_NAME,
              `completed adapting page: ${cmsPageName}`,
              {
                totalComponents: pageComponents?.length,
                personalizedComponents: personalizedComponents?.length,
                queryParamsRequired
              }
            );
          }

          pagesData[cmsPageName] = {
            components: pageComponents,
            pageProperties,
            rawPageProperties,
            personalizedComponents,
            personalizationRedirects,
            pageTheme,
            pageBackground,
            flashComponent,
            oem,
            pageLevelGlobalHeader,
            pageLevelLinkFarm,
            pageLevelGlobalFooter,
            queryParamsRequired
          };
        }
      } catch (error) {
        logError(ADAPTER_NAME, error, {
          cmsPageName
        });
      }
    });
    return pagesData;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      pagesCount: args?.pagesCache?.length,
      pagesInfoCount: args?.batchPagesInfo?.length
    });
  }
}

function getBreadcrumbdata(pageData: ComponentData[]) {
  if (pageData) {
    const pageThemeKey = Object.keys(pageData).find(
      key => pageData[key].type === "PAGETHEME"
    );
    const pagePropertyKey = Object.keys(pageData).find(
      key => pageData[key].type === "PAGEPROPERTY"
    );
    const pageTheme = pageThemeKey ? pageData[pageThemeKey] : {};
    const pageProperty = pagePropertyKey ? pageData[pagePropertyKey] : {};
    const breadcrumbTitle = pageProperty?.seo?.[0]?.breadcrumbTitle;
    const disableBreadCrumbsTop = pageProperty?.seo?.[0]?.disableBreadCrumbsTop;
    const disableBreadCrumbs = pageProperty?.seo?.[0]?.disableBreadCrumbs;
    const isNewTheme = pageTheme?.themeName === "newTheme";
    const type = isNewTheme ?
      ComponentNames.BREADCRUMBSV2 :
      ComponentNames.BREADCRUMBS;
    let breadcrumb;

    const isBreadCrumExist = Object.values<ComponentData>(pageData).some(
      comp => comp && comp.type === type
    );
    if (!isBreadCrumExist) {
      breadcrumb = {
        id: "breadcrumbs",
        type
      };
    }

    return {
      isDoubleBreadcrumbNeeded: pageProperty.isDoubleBreadcrumbNeeded,
      breadcrumb,
      breadcrumbTitle,
      disableBreadCrumbsTop,
      disableBreadCrumbs
    };
  }
}

function handleBreadcrumbs(
  pageComponents: any[],
  disableBreadCrumbs: boolean,
  disableBreadCrumbsTop: boolean,
  applicationFeatureConfig: any
) {
  try {
    // Add or Remove Top & Bottom Breadcrumbs
    const bcData =
      pageComponents[pageComponents.length - 1]?.type === "BREADCRUMBSV2" ?
        pageComponents[pageComponents.length - 1] :
        null;

    // Remove Bottom Breadcrumbs if disabled in PageProperty
    try {
      if (
        disableBreadCrumbs &&
        pageComponents[pageComponents.length - 1]?.type === "BREADCRUMBSV2"
      ) {
        pageComponents.pop();
      }
    } catch (error) {
      logAdapterEvent(ADAPTER_NAME, error, {
        step: "Error while removing bottom breadcrumbs"
      });
    }

    try {
      if (
        bcData &&
        applicationFeatureConfig?.breadcrumbsOn &&
        !disableBreadCrumbsTop
      ) {
        const updatedBcData = {
          ...bcData,
          props: {
            ...bcData?.props,
            ...{ isTopBreadcrumb: true }
          }
        };
        // Add Top Breadcrumbs after Marketing Banner Component if it is the first component on page
        const marketingBannerIndex = pageComponents?.findIndex(
          c => c?.type === ComponentNames?.MARKETINGBANNER
        );
        if (marketingBannerIndex === 0) {
          // MarketingBanner is the first component — insert breadcrumb after it
          pageComponents?.splice(1, 0, updatedBcData);
        } else {
          // Add Top Breadcrumbs on Top always if Marketing Banner is not the first component on page
          pageComponents?.unshift(updatedBcData);
        }
      }
    } catch (error) {
      logAdapterEvent(ADAPTER_NAME, error, {
        step: "Error while adding top breadcrumbs"
      });
    }
  } catch (error) {
    logAdapterEvent(ADAPTER_NAME, error, {
      step: "Error while handling breadcrumbs"
    });
  }
}

interface IPersonalizedVariantContext {
  index: number;
  sbPreviewAllowed?: boolean;
  modalPriorityMap?: any;
  packagesConfig?: any;
  cmsPageName?: string;
  rsnTierLegalTextObj?: any;
  epochOffers?: any;
  dtviOffersPrices?: any;
  svodGenreData?: any;
  embeddedSvods?: any[];
}

/**
 * WAT-10763: Resolve a personalized (non-default) variant's component through
 * its component-specific adapter so Epoch price placeholders ([offer_price],
 * [regular_price], [promo_duration], …) are resolved exactly as they are on the
 * default (non-personalized) render path.
 *
 * Previously PZN variant components bypassed the main page-adapter switch and
 * received only commonSbFixer() cleanup, which has no Epoch data — leaking
 * literal placeholder tokens to the customer when the variant was swapped or
 * injected. This routes the marketingBanner / dtvMarquee variants through the
 * same adapters (with the same build-time Epoch data) the main switch uses.
 *
 * Component types without a dedicated pricing adapter fall through to
 * commonSbFixer — byte-for-byte the prior behavior, so out-of-scope components
 * (plan-card, package-carousel, future variants) are unaffected.
 */
export function adaptPersonalizedVariantComponent(
  components: any,
  ctx: IPersonalizedVariantContext
) {
  const variantComponent = Array.isArray(components) ? components[0] : components;

  switch (variantComponent?.type) {
    case ComponentNames.MARKETINGBANNER:
      return marketingBannerAdapter({
        content: variantComponent,
        index: ctx.index,
        sbPreviewAllowed: ctx.sbPreviewAllowed,
        modalPriorityMap: ctx.modalPriorityMap,
        packagesConfig: ctx.packagesConfig,
        cmsPageName: ctx.cmsPageName,
        rsnTierLegalTextObj: ctx.rsnTierLegalTextObj,
        genreSvodOffers: ctx.epochOffers?.genreSvodOffers,
        getOffersData: ctx.dtviOffersPrices,
        getOffersDTVsData: ctx.epochOffers?.dtvsOffersPrices,
        packageConfig: ctx.packagesConfig
      });
    case ComponentNames.DTVMARQUEE:
      return dtvMarqueeAdapter({
        content: variantComponent,
        svodGenreData: ctx.svodGenreData,
        embeddedSvods: ctx.embeddedSvods,
        epochOffers: ctx.epochOffers,
        cmsPageName: ctx.cmsPageName,
        packagesConfig: ctx.packagesConfig
      });
    default:
      return commonSbFixer(components);
  }
}

function getPersonalizedData(data: any) {
  try {
    logAdapterEvent(ADAPTER_NAME, "Extracting personalized content");
    const variants = data?.variants || [];
    const defaultVariant = variants?.find?.((v: any) => v.name === "default");
    const defaultVariants = defaultVariant?.components?.[0];
    const defaultAnalytics = defaultVariant?.analytics;
    const personalizationComponents = variants.filter(
      (v: any) => v.name !== "default"
    );
    return {
      defaultVariants: defaultVariants || null,
      defaultAnalytics: defaultAnalytics || null,
      personalizationComponents
    };
  } catch (error) {
    logAdapterEvent(ADAPTER_NAME, error, {
      step: "Error while handling personalized content"
    });
    return {
      defaultVariants: null,
      defaultAnalytics: null,
      personalizationComponents: []
    };
  }
}

function convertArrayObjectToObject(array, keyName) {
  const obj = {};
  array.forEach(item => {
    const key = item[keyName];
    obj[key] = item;
  });
  return obj;
}
